import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import clsx from 'clsx';
import { Drawer } from '../shared/Drawer.jsx';
import { useCreateUser, useUpdateUser } from '../../hooks/useUserMutations.js';
import { usePermissions, useUserPermissions } from '../../hooks/usePermissions.js';
import { useCashboxes } from '../../../cashbox/hooks/useCashboxes.js';
import { apiError, fieldErrors } from '../../../../shared/api.js';
import { createUserSchema, editUserSchema } from './userSchema.js';
import { PermissionEditor } from './PermissionEditor.jsx';

function generatePassword(length = 10) {
  const letters = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
  const digits = '23456789';
  const all = letters + digits;
  const chars = [
    letters[Math.floor(Math.random() * letters.length)],
    digits[Math.floor(Math.random() * digits.length)]
  ];
  for (let i = chars.length; i < length; i += 1) {
    chars.push(all[Math.floor(Math.random() * all.length)]);
  }
  return chars.sort(() => Math.random() - 0.5).join('');
}

function defaults(u) {
  return {
    fullName: u?.fullName || '',
    username: u?.username || '',
    email: u?.email || '',
    phone: u?.phone || '',
    roleId: u?.roleId?._id || u?.roleId || u?.role?._id || u?.role?.id || '',
    assignedCashboxId: u?.assignedCashboxId?._id || u?.assignedCashboxId || '',
    status: u?.status || (u?.isActive === false ? 'inactive' : 'active'),
    password: '',
    permissionsMode: 'role',
    permissions: []
  };
}

function fieldClass(hasError) {
  return clsx('input', hasError && 'border-red border-2');
}

// يقارن مجموعة صلاحيات المستخدم بصلاحيات الدور لتحديد ما إذا كانت "افتراضية" أو "مخصصة".
function arraysMatch(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  return b.every((x) => sa.has(x));
}

export function UserDrawer({ open, user, roles, onClose }) {
  const isEdit = !!user;
  const userId = user?._id || user?.id || null;

  const create = useCreateUser({ errorToast: false });
  const update = useUpdateUser({ errorToast: false });
  const [generated, setGenerated] = useState('');
  const [topError, setTopError] = useState('');

  const { data: groups = [], isLoading: groupsLoading } = usePermissions();
  const { data: cashboxes = [] } = useCashboxes();
  const userPermsQuery = useUserPermissions(userId, open && isEdit);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    control,
    formState: { errors }
  } = useForm({
    defaultValues: defaults(user),
    resolver: joiResolver(isEdit ? editUserSchema : createUserSchema),
    mode: 'onBlur'
  });

  const watchedRoleId = useWatch({ control, name: 'roleId' });
  const watchedMode = useWatch({ control, name: 'permissionsMode' });
  const watchedPermissionsRaw = useWatch({ control, name: 'permissions' });
  const watchedPermissions = Array.isArray(watchedPermissionsRaw) ? watchedPermissionsRaw : [];

  const selectedRole = useMemo(() => {
    const list = Array.isArray(roles) ? roles : [];
    return list.find((r) => String(r._id || r.id) === String(watchedRoleId)) || null;
  }, [roles, watchedRoleId]);

  useEffect(() => {
    if (open) {
      reset(defaults(user));
      setGenerated('');
      setTopError('');
      clearErrors();
    }
  }, [open, user, reset, clearErrors]);

  // عند فتح نموذج التعديل وتحميل صلاحيات المستخدم:
  // - إن طابقت صلاحيات الدور → نضع الوضع "افتراضي".
  // - إن اختلفت → "مخصص" وتعبئة المصفوفة بصلاحيات المستخدم الفعلية.
  useEffect(() => {
    if (!open || !isEdit) return;
    if (userPermsQuery.isLoading || !userPermsQuery.data) return;
    const userPerms = Array.isArray(userPermsQuery.data) ? userPermsQuery.data : [];
    const rolePerms = Array.isArray(selectedRole?.permissions) ? selectedRole.permissions : [];
    const hasWildcard = rolePerms.includes('*') || userPerms.includes('*');
    if (hasWildcard) {
      setValue('permissionsMode', 'role', { shouldDirty: false });
      setValue('permissions', userPerms, { shouldDirty: false });
      return;
    }
    if (arraysMatch(userPerms, rolePerms)) {
      setValue('permissionsMode', 'role', { shouldDirty: false });
      setValue('permissions', [], { shouldDirty: false });
    } else {
      setValue('permissionsMode', 'custom', { shouldDirty: false });
      setValue('permissions', userPerms, { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, userPermsQuery.data, userPermsQuery.isLoading]);

  function regen() {
    const pwd = generatePassword();
    setGenerated(pwd);
    setValue('password', pwd, { shouldValidate: true });
  }

  function applyServerErrors(err) {
    const map = fieldErrors(err);
    let matched = false;
    for (const [name, message] of Object.entries(map)) {
      setError(name, { type: 'server', message });
      matched = true;
    }
    setTopError(matched ? '' : apiError(err));
  }

  const submit = handleSubmit(async (values) => {
    setTopError('');
    const body = {
      fullName: values.fullName.trim(),
      username: values.username.trim(),
      email: values.email.trim() || undefined,
      phone: values.phone.trim() || undefined,
      roleId: values.roleId || undefined,
      assignedCashboxId: values.assignedCashboxId || null,
      status: values.status
    };
    if (!isEdit) {
      body.password = values.password || generatePassword();
    }
    // الصلاحيات: تُرسل فقط في وضع "مخصص" — في الوضع الافتراضي تُحمَّل تلقائياً من الدور في الخادم.
    if (values.permissionsMode === 'custom') {
      body.permissions = Array.from(new Set(values.permissions || []));
    } else if (isEdit) {
      // «افتراضي من الدور»: نطلب صراحةً إعادة الصلاحيات لصلاحيات الدور،
      // وإلا بقيت الصلاحيات المخصّصة السابقة كما هي.
      body.permissionsMode = 'role';
    }
    try {
      if (isEdit) {
        await update.mutateAsync({ id: userId, body });
      } else {
        await create.mutateAsync(body);
      }
      onClose?.();
    } catch (err) {
      applyServerErrors(err);
    }
  });

  const submitting = create.isPending || update.isPending;

  return (
    <Drawer
      open={open}
      title={isEdit ? 'تعديل مستخدم' : 'مستخدم جديد'}
      onClose={onClose}
      width={640}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={submitting}>
            إلغاء
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? 'جاري الحفظ...' : isEdit ? 'حفظ' : 'إنشاء'}
          </button>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        {topError ? (
          <div className="p-2.5 rounded-[10px] bg-red/10 border border-red/30 text-red text-[12px]">
            {topError}
          </div>
        ) : null}

        <div className="space-y-3">
          <div className="text-[11px] font-bold text-ink-3 tracking-wider">المعلومات الأساسية</div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">الاسم الكامل *</label>
            <input
              className={fieldClass(errors.fullName)}
              placeholder="مثلاً: سامر العتابي"
              {...register('fullName')}
            />
            {errors.fullName ? (
              <div className="text-red text-[11px] mt-1">{errors.fullName.message}</div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">اسم الدخول *</label>
              <input
                className={fieldClass(errors.username)}
                placeholder="username"
                disabled={isEdit}
                {...register('username')}
              />
              {errors.username ? (
                <div className="text-red text-[11px] mt-1">{errors.username.message}</div>
              ) : null}
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الدور *</label>
              <select
                className={fieldClass(errors.roleId)}
                {...register('roleId')}
              >
                <option value="">اختر دوراً</option>
                {roles.map((r) => (
                  <option key={r._id || r.id} value={r._id || r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              {errors.roleId ? (
                <div className="text-red text-[11px] mt-1">{errors.roleId.message}</div>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">البريد</label>
              <input
                className={fieldClass(errors.email)}
                type="email"
                placeholder="name@example.com"
                {...register('email')}
              />
              {errors.email ? (
                <div className="text-red text-[11px] mt-1">{errors.email.message}</div>
              ) : null}
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الهاتف</label>
              <input
                className={fieldClass(errors.phone)}
                placeholder="07XXXXXXXXX"
                {...register('phone')}
              />
              {errors.phone ? (
                <div className="text-red text-[11px] mt-1">{errors.phone.message}</div>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">الحالة</label>
              <select className="input" {...register('status')}>
                <option value="active">نشط</option>
                <option value="inactive">معطّل</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">القاصة المنسبة</label>
              <select className="input" {...register('assignedCashboxId')}>
                <option value="">— بدون تنسيب —</option>
                {cashboxes
                  .filter((c) => c.isActive !== false)
                  .map((c) => (
                    <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>
                  ))}
              </select>
            </div>
          </div>

          {!isEdit ? (
            <div>
              <label className="block text-xs font-semibold text-ink-2 mb-1">كلمة المرور *</label>
              <div className="flex items-center gap-2">
                <input
                  className={fieldClass(errors.password)}
                  placeholder="أدخل كلمة المرور أو ولّدها تلقائياً"
                  {...register('password')}
                />
                <button type="button" className="btn" onClick={regen}>
                  توليد
                </button>
              </div>
              <div className="text-[11px] text-ink-3 mt-1">
                8 أحرف على الأقل، تحتوي على حروف وأرقام.
              </div>
              {errors.password ? (
                <div className="text-red text-[11px] mt-1">{errors.password.message}</div>
              ) : null}
              {generated ? (
                <div className="text-[11px] text-ink-3 mt-1">
                  كلمة مرور مُولَّدة: <span className="font-mono font-bold">{generated}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <PermissionEditor
          mode={watchedMode}
          onModeChange={(m) => setValue('permissionsMode', m, { shouldDirty: true })}
          selected={watchedPermissions}
          onSelectedChange={(next) => setValue('permissions', next, { shouldDirty: true })}
          groups={groups}
          role={selectedRole}
          loadingGroups={groupsLoading}
          loadingUserPerms={isEdit && userPermsQuery.isLoading}
          disabled={submitting}
        />
      </form>
    </Drawer>
  );
}
